import React from "react";
import {
  Box,
  Typography,
  Divider,
  Stack,
  Paper,
  useTheme,
  Link,
  Avatar,
  Chip,
  Grid,
  Card,
  CardContent,
  Container,
  alpha,
  Button
} from "@mui/material";
import {
  Code as CodeIcon,
  Storage as StorageIcon,
  GitHub as GitHubIcon,
  Language as LanguageIcon,
  Public as PublicIcon,
  Update as UpdateIcon,
  AltRoute as RouteIcon,
  AccountCircle as AccountCircleIcon,
  Verified as VerifiedIcon,
  Speed as SpeedIcon,
  Security as SecurityIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Assignment as AssignmentIcon,
  Timeline as TimelineIcon,
  AccountBalance as AccountBalanceIcon,
  Support as SupportIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon
} from "@mui/icons-material";
import { breadCrumbsStore } from "../Components/BreadCrumbs/BreadCrumbsListner";

const AboutPage: React.FC = () => {
  breadCrumbsStore.updateBreadCrumbs([{ "About": "/aboutus" }]);

  const theme = useTheme();
  const currentYear = new Date().getFullYear();

  const features = [
    { icon: PeopleIcon, title: "User Management", description: "Role-based access control with multi-level hierarchy" },
    { icon: AccountBalanceIcon, title: "Loan Processing", description: "End-to-end loan application, sanctioning & disbursement" },
    { icon: TimelineIcon, title: "Repayment Monitoring", description: "Track repayments and generate automated reminders" },
    { icon: AssignmentIcon, title: "Monthly Reports", description: "Comprehensive reports and audit logs" },
    { icon: TrendingUpIcon, title: "Analytics Dashboard", description: "Real-time insights and performance metrics" },
    { icon: SecurityIcon, title: "Secure Access", description: "Multi-factor authentication and data encryption" }
  ];

  const techStack = [
    {
      icon: CodeIcon,
      label: "Frontend",
      value: "React 18 + TypeScript + MUI v5 + Electron.js Desktop",
      color: "#61DAFB"
    },
    {
      icon: StorageIcon,
      label: "Backend",
      value: ".NET 8 Web API + Entity Framework + SQL Server",
      color: "#512BD4"
    },
    {
      icon: GitHubIcon,
      label: "DevOps",
      value: "GitHub + CI/CD + Automated Builds",
      color: "#333"
    },
    {
      icon: SpeedIcon,
      label: "Performance",
      value: "Optimized APIs + Server-side Pagination",
      color: "#DC382C"
    }
  ];

  return (
    <Box sx={{
      bgcolor: alpha(theme.palette.primary.main, 0.02),
      minHeight: "100vh",
      py: 4,
      px: { xs: 2, sm: 4 }
    }}>
      <Container maxWidth="lg">
        {/* Hero Section */}
        <Paper
          elevation={0}
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            borderRadius: 4,
            p: { xs: 4, md: 6 },
            mb: 4,
            color: 'white',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Chip
              label="Version 1.0.0"
              size="small"
              sx={{
                mb: 2,
                backgroundColor: alpha('#fff', 0.2),
                color: 'white',
                border: '1px solid rgba(255,255,255,0.3)'
              }}
            />
            <Typography variant="h3" fontWeight={700} gutterBottom>
              Sales Management System
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9, mb: 3, maxWidth: 600 }}>
              Empowering Business's Self-Help Groups Across Kerala
            </Typography>
            {/* <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
              <Button 
                variant="contained" 
                sx={{ 
                  bgcolor: 'white', 
                  color: theme.palette.primary.main,
                  '&:hover': { bgcolor: alpha('#fff', 0.9) }
                }}
              >
                Documentation
              </Button>
              <Button 
                variant="outlined" 
                sx={{ 
                  borderColor: 'white', 
                  color: 'white',
                  '&:hover': { borderColor: 'white', bgcolor: alpha('#fff', 0.1) }
                }}
              >
                Release Notes
              </Button>
            </Stack> */}
          </Box>

          {/* Decorative Elements */}
          <Box
            sx={{
              position: 'absolute',
              top: -20,
              right: -20,
              width: 200,
              height: 200,
              borderRadius: '50%',
              background: alpha('#fff', 0.1),
              zIndex: 0
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: -40,
              left: -40,
              width: 300,
              height: 300,
              borderRadius: '50%',
              background: alpha('#fff', 0.05),
              zIndex: 0
            }}
          />
        </Paper>

        {/* Main Content */}
        <Grid container spacing={4}>
          {/* Left Column - Company Info */}
          <Grid item xs={12} md={5}>
            <Paper
              elevation={0}
              sx={{
                p: 4,
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                height: '100%'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
                <Avatar
                  src="/logo.png"
                  alt="Willsoar Technologies"
                  sx={{
                    width: 64,
                    height: 64,
                    bgcolor: "transparent"
                  }}
                />

                <Box>
                  <Typography variant="h5" fontWeight={600} gutterBottom>
                    Willsoar Technologies
                  </Typography>

                  <Link
                    href="https://willsoartechnologies.com/"
                    target="_blank"
                    underline="hover"
                    sx={{ color: theme.palette.primary.main }}
                  >
                    www.willsoartechnologies.com
                  </Link>
                </Box>
              </Box>

              <Stack spacing={2}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <VerifiedIcon sx={{ color: theme.palette.success.main }} />
                  <Typography>
                    <strong>Release Name:</strong> KLS-Launch
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <UpdateIcon sx={{ color: theme.palette.info.main }} />
                  <Typography>
                    <strong>Last Update:</strong> July 21, 2025
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <PublicIcon sx={{ color: theme.palette.warning.main }} />
                  <Typography>
                    <strong>Language:</strong> English (en-IN)
                  </Typography>
                </Box>
              </Stack>

              <Divider sx={{ my: 4 }} />

              <Typography variant="h6" fontWeight={600} gutterBottom>
                Contact Information
              </Typography>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <EmailIcon sx={{ color: theme.palette.text.secondary }} />
                  <Typography>info@willsoar.in</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <PhoneIcon sx={{ color: theme.palette.text.secondary }} />
                  <Typography>+91 8304 912033</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <LocationIcon sx={{ color: theme.palette.text.secondary }} />
                  <Typography>Kinfra Mattanur PO, 670702</Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>

          {/* Right Column - Tech Stack & Features */}
          <Grid item xs={12} md={7}>
            <Paper
              elevation={0}
              sx={{
                p: 4,
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                mb: 4
              }}
            >
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Technology Stack
              </Typography>
              <Grid container spacing={3} sx={{ mt: 1 }}>
                {techStack.map((tech, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar
                        sx={{
                          width: 40,
                          height: 40,
                          bgcolor: alpha(tech.color, 0.1),
                          color: tech.color
                        }}
                      >
                        <tech.icon />
                      </Avatar>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {tech.label}
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {tech.value}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                p: 4,
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
              }}
            >
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Key Features
              </Typography>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                {features.map((feature, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Card
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`,
                          borderColor: theme.palette.primary.main
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Avatar
                          sx={{
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main
                          }}
                        >
                          <feature.icon />
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" fontWeight={600}>
                            {feature.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {feature.description}
                          </Typography>
                        </Box>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>
        </Grid>

        {/* Footer */}
        <Paper
          elevation={0}
          sx={{
            mt: 4,
            p: 3,
            borderRadius: 2,
            background: `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.02)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
          }}
        >
          <Typography variant="body2" color="text.secondary" align="center">
            © {currentYear} Willsoar Technologies. All rights reserved.
          </Typography>
          <Typography variant="caption" color="text.secondary" align="center" sx={{ display: 'block', mt: 1 }}>
            This software is developed to streamline loan processing, improve transparency,
            and empower business's self-help groups (SHGs) across Kerala.
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default AboutPage;